import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { PieChart as PieChartIcon } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { useTierDistribution, type TierDistributionSlice } from '@/hooks/useDashboardAnalytics';

interface TierDistributionWidgetProps {
  className?: string;
}

const TIER_COLORS = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#f59e0b', // amber
  '#a855f7', // purple
  '#ec4899', // pink
  '#14b8a6', // teal
  '#94a3b8', // slate (fallback / Other)
] as const;

interface SliceDatum {
  key: string;
  label: string;
  value: number;
  percentage: number;
}

interface TooltipPayloadItem {
  payload?: SliceDatum;
}

interface PieTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}

function PieTooltip({ active, payload }: PieTooltipProps) {
  if (!active || !payload || payload.length === 0) {return null;}
  const datum = payload[0]?.payload;
  if (!datum) {return null;}
  return (
    <div className="bg-popover border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium">{datum.label}</p>
      <p>Активных подписок: {datum.value}</p>
      <p className="text-muted-foreground">{datum.percentage}% от всех</p>
    </div>
  );
}

function sliceKey(slice: TierDistributionSlice, index: number): string {
  if (slice.tierId !== null) {
    return `tier-${slice.tierId}`;
  }
  return `tier-null-${index}`;
}

export function TierDistributionWidget({ className }: TierDistributionWidgetProps) {
  const { data, isLoading } = useTierDistribution();

  const tiers = useMemo(() => data?.tiers ?? [], [data?.tiers]);

  const chartData = useMemo<SliceDatum[]>(() => {
    return tiers.map((slice, i) => ({
      key: sliceKey(slice, i),
      label: slice.label,
      value: slice.count,
      percentage: slice.percentage,
    }));
  }, [tiers]);

  const totalActive = useMemo(
    () => chartData.reduce((sum, d) => sum + d.value, 0),
    [chartData],
  );

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <PieChartIcon className="h-4 w-4" />
            Тарифы подписок
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[220px] w-full rounded-full" />
        </CardContent>
      </Card>
    );
  }

  if (chartData.length === 0 || totalActive === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <PieChartIcon className="h-4 w-4" />
            Тарифы подписок
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            Нет активных подписок
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <PieChartIcon className="h-4 w-4" />
          Тарифы подписок
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Активные подписки по длительности
        </p>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={85}
                paddingAngle={2}
              >
                {chartData.map((datum, i) => (
                  <Cell
                    key={datum.key}
                    fill={TIER_COLORS[i] ?? TIER_COLORS.at(-1) ?? '#94a3b8'}
                  />
                ))}
              </Pie>
              <Tooltip content={<PieTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-2xl font-bold tabular-nums">{totalActive}</span>
            <span className="text-xs text-muted-foreground">всего</span>
          </div>
        </div>
        <div className="mt-4 space-y-1.5">
          {chartData.map((datum, i) => (
            <div key={datum.key} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm flex-shrink-0"
                  style={{
                    backgroundColor: TIER_COLORS[i] ?? TIER_COLORS.at(-1) ?? '#94a3b8',
                  }}
                />
                <span className="truncate">{datum.label}</span>
              </div>
              <span className="tabular-nums text-muted-foreground flex-shrink-0">
                {datum.value} · {datum.percentage}%
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
